/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Button, Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import {
  generateOptions,
  getCurrencyBasedMarketData,
  getUserTimezone,
  opacity2hex,
  serializeDateValue,
  sortArray,
  startOfWeek,
  wrapWithAll,
} from "utils/miscUtils";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { SimpleSelectInput } from "components/FormSelectInput";
import { marketReportColumns } from "utils/tableHelper/reportsTableHelper";
import { exportReport, getMarketReportList, ReportTypes } from "services/reportsService";
import { SimpleTextInput } from "components/FormTextInput";
import useFilterOptionsHook from "hooks/useFilterOptions";
import { SimpleDateInput } from "components/FormDateInput";
import {
  CurrencyOptions,
  DefaultCurrency,
  PBO_InitialValues,
} from "utils/constants";
import ExportModal from "screens/modals/ExportModal";
import useOpenClose from "hooks/useOpenClose";
import UploadIcon from "@mui/icons-material/Upload";
import { FilterSectionProps } from "types/filters";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "fit-content",
    flexFlow: "row wrap",
    gap: 4,
  },
  field: {
    width: 180,
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 24,
    gap: 5,
    marginLeft: 10,
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  icon: {
    fontSize: "20px",
  },
}));

const RowActions = ({ data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(
      configData.routes.playerDetails.editPlayerCard?.replace(":id", data?._id)
    );
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
      </Menu>
    </>
  );
};

const FilterSection = ({
  handleChange = () => { },
  onSubmitClick = () => { },
  onReset = () => { },
  filterOptions,
}: FilterSectionProps) => {
  const classes = useStyles();
  const marketTypeOptions = generateOptions({
    items: sessionHelper.marketTypeOptions,
    id: "marketName",
    label: "name",
  });

  const customStyle = { height: 30 };

  return (
    <Box className={classes.root}>
      <Box className={classes.field} style={{ marginLeft: "0px" }}>
        <SimpleDateInput
          label="From:"
          id="startTime"
          name="startTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.startTime}
          onChange={(date) =>
            handleChange({ name: "startTime", value: date })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleDateInput
          label="To:"
          id="endTime"
          name="endTime"
          fullWidth
          format="DD-MM-YYYY"
          variant="inline"
          removeBottomMargin={true}
          value={filterOptions.endTime}
          onChange={(date) =>
            handleChange({ name: "endTime", value: date })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleSelectInput
          name="platform"
          label="Platform"
          placeholdertext="Select Platforms"
          options={wrapWithAll(sessionHelper.platformOptions)}
          value={filterOptions.platform}
          onChange={(e) =>
            handleChange({ name: "platform", value: e?.target?.value })
          }
          customStyle={customStyle}
        />
      </Box>
      {![null, "all"].includes(filterOptions.platform) && (
        <Box className={classes.field}>
          <SimpleSelectInput
            name="operator"
            label="Operator"
            options={wrapWithAll(
              sessionHelper.getFilteredOperators({
                platformIds: [filterOptions.platform],
              })
            )}
            value={filterOptions.operator}
            placeholder="Select Operators"
            onChange={(e) =>
              handleChange({ name: "operator", value: e?.target?.value })
            }
            customStyle={customStyle}
          />
        </Box>
      )}
      {![null, "all"].includes(filterOptions.operator) && (
        <Box className={classes.field}>
          <SimpleSelectInput
            name="brand"
            label="Brand"
            options={wrapWithAll(
              sessionHelper.getFilteredBrands({
                platformIds: [filterOptions.platform],
                operatorIds: [filterOptions.operator],
              })
            )}
            value={filterOptions.brand}
            onChange={({ target: { name, value } }) =>
              handleChange({ name, value })
            }
            customStyle={customStyle}
          />
        </Box>
      )}
      <Box className={classes.field}>
        <SimpleSelectInput
          name="marketName"
          label="Market Type"
          options={marketTypeOptions}
          value={filterOptions.marketName}
          onChange={(e) =>
            handleChange({ name: "marketName", value: e?.target?.value })
          }
          customStyle={{ height: 30 }}
        />
      </Box>{" "}
      <Box className={classes.field}>
        <SimpleTextInput
          onChange={(e) =>
            handleChange({ name: "externalPlayerId", value: e?.target?.value })
          }
          customStyle={{ height: 30 }}
          type="text"
          label="External Player Id"
          name="externalPlayerId"
          value={filterOptions.externalPlayerId}
        />
      </Box>
      <Box className={classes.field}>
        <SimpleSelectInput
          name="currency"
          label="Currency"
          options={CurrencyOptions}
          value={filterOptions.currency}
          onChange={(e) =>
            handleChange({ name: "currency", value: e?.target?.value })
          }
          customStyle={customStyle}
        />
      </Box>
      <Box className={classes.buttonContainer}>
        <Button className={classes.button} onClick={onSubmitClick}>
          Submit
        </Button>
        <Button className={classes.button} onClick={onReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};

const MarketReport = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  // const { searchValue, onSearch: _onSearch } = useSearchHook();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.CARD.UPDATE_ROLES
  );
  const [columns, setColumns] = useState([]);
  const {
    filterOptions,
    onChange: setFilterOptions,
    reset,
  } = useFilterOptionsHook({
    initialValues: {
      startTime: startOfWeek(),
      endTime: new Date(),
      currency: DefaultCurrency,
      externalPlayerId: null,
      marketName: "",
      ...PBO_InitialValues,
    },
  });

  const { isOpen, open, close } = useOpenClose();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const buildReportPayload = () => {
    const { startTime, endTime, ...rest } = filterOptions;
    let _start = new Date(startTime);
    let _end = new Date(endTime);
    if (_start > _end) {
      showSnackbar("Start date should not be greater than end date", "error");
      return false;
    }

    return {
      ...rest,
      pageNo,
      pageSize,
      startTime: serializeDateValue(startTime),
      endTime: serializeDateValue(endTime),
      timezone: getUserTimezone(),
    };
  }

  const fetchMarketReport = async () => {
    setLoading(true);
    try {
      const payload = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const response = await getMarketReportList(payload);
      const marketData = response?.data?.marketWiseSummary;

      let updatedData = getCurrencyBasedMarketData(
        marketData,
        filterOptions.currency
      );
      setList(marketData);
      setFilteredList(updatedData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  const onExportReportMail = async (email) => {
    setLoading(true);
    try {
      let payload = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const { platform, operator, brand } = payload

      if (platform === "all") payload.platform = null;
      if (operator === "all") payload.operator = null;
      if (brand === "all") payload.brand = null;

      payload = { ...payload, type: ReportTypes.market, email }

      await exportReport(payload);

      setLoading(false);
      showSnackbar("Data request, will be sent to the email.");
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  }

  const handleFilterChange = ({ name, value }: any) => {
    setFilterOptions({ name, value });

    if (name === "currency") {
      const _data = getCurrencyBasedMarketData(list, value);
      setFilteredList(_data);
    }
  };

  useEffect(() => {
    fetchMarketReport();
  }, [pageNo, pageSize]);

  useEffect(() => {
    const initialColumns = marketReportColumns(filterOptions).filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);
  }, [filterOptions.currency]);

  return (
    <ContentShell title="Market Report" loading={loading} actions={
      <Button
        startIcon={<UploadIcon className={styles.icon} />}
        variant="contained"
        onClick={open}
      >
        Export
      </Button>
    }>
      <Box className={styles.playerCardParent}>
        <FilterSection
          filterOptions={filterOptions}
          handleChange={handleFilterChange}
          onSubmitClick={fetchMarketReport}
          onReset={reset}
        />
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          highlightAltRow={true}
          actions={RowActions}
          onColumnClick={onColumnClick}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{}}
        />
      </Box>
      <ExportModal
        list={list}
        fileName="Daily_Report"
        filterOptions={filterOptions}
        open={isOpen}
        onClose={close}
        onSendEmail={onExportReportMail}
      />
    </ContentShell>
  );
};

export default MarketReport;
